import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  getActiveMemberQueryOptions,
  getOrganizationQueryOptions,
} from '@/api/organizations/queries';
import { useGetSubscription } from '@/api/subscription/queries';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UpgradeLink } from '@/components/upgrade-link';
import { InviteMemberDialog } from './-components/invite-member-dialog';
import { MembersTable } from './-components/members-table';
import { PendingInvitationsTable } from './-components/pending-invitations-table';

export const Route = createFileRoute('/_authenticated/_global/members/')({
  component: MembersPage,
  loader: async ({ context: { queryClient } }) => {
    await Promise.all([
      queryClient.ensureQueryData(getOrganizationQueryOptions),
      queryClient.ensureQueryData(getActiveMemberQueryOptions),
    ]);
  },
});

function MembersPage() {
  const { data } = useSuspenseQuery(getOrganizationQueryOptions);
  const { data: activeMember } = useSuspenseQuery(getActiveMemberQueryOptions);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const { data: subscription } = useGetSubscription();
  const isFreePlan = subscription?.isFree;
  const isInviteEnabled = !isFreePlan && activeMember.role !== 'member';

  return (
    <PageContainer>
      <PageHeader
        title="Team Members"
        description="Manage your team members and their permissions"
        rightControls={
          !isInviteEnabled ? null : (
            <InviteMemberDialog isOpen={isInviteOpen} onOpenChange={setIsInviteOpen} />
          )
        }
      />
      {isFreePlan && (
        <div className="flex items-start justify-self-start flex-col gap-3 text-orange-800 dark:text-orange-200 mb-6 border border-orange-600/50 dark:border-orange-300/50 bg-orange-900/5 rounded-lg p-4">
          <div className="flex w-full flex-1 items-center gap-2 justify-between">
            <p className="font-semibold">Limited functionality</p>
            <UpgradeLink variant="small" />
          </div>
          <p className="text-sm">
            You are on the free plan. Upgrade to a paid plan to invite more members and manage
            permissions.
          </p>
        </div>
      )}
      <Tabs defaultValue="members" className="space-y-6">
        <TabsList>
          <TabsTrigger value="members">
            Team Members
            <span className="text-muted-foreground">{data.members?.length || 0}</span>
          </TabsTrigger>
          <TabsTrigger value="invites">
            Pending Invites{' '}
            <span className="text-muted-foreground">{data.invitations?.length || 0}</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="members">
          <MembersTable members={data.members || []} activeMember={activeMember} />
        </TabsContent>
        <TabsContent value="invites">
          <PendingInvitationsTable invitations={data.invitations || []} />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
